/**
 * 🍖 Health and food, as the client sees them (T129).
 *
 * **The server owns the two bars** and corrects them every thirty seconds. This
 * carries them forward in between, so the bars drain smoothly instead of
 * stepping once every half minute — exactly what `ClientWorldClock` does for the
 * date, and for the same reason.
 *
 * 🔴 **It does not have its own copy of the rules.** `stepVitals` comes from
 * `@lofi/shared`, which is the same function the server ticks with, so the bar
 * being drawn and the number in the database cannot drift apart by disagreeing
 * about arithmetic. ⚠️ **This is the difference between carrying forward and
 * predicting.** The client is not guessing what the server will decide; it is
 * running the server's own decision between corrections, and being overruled
 * whenever one arrives.
 *
 * ⚠️ **It never invents a value.** Before the first `vitals` message it answers
 * `null` and the HUD keeps drawing full, because a new player is neither hurt
 * nor hungry and a bar that starts empty and fills a second later looks exactly
 * like the fault this task set out to fix.
 */

import {
  EXPOSURE_FACTORS,
  stepVitals,
  vitalsReason,
  type ExposureFactor,
  type Vitals,
  type VitalsReasonKey,
} from '@lofi/shared';

export class ClientVitals {
  /** The server's answer at the last correction. */
  private atSync: Vitals | null = null;
  /**
   * `performance.now()` when it arrived, or `null` before the first.
   *
   * ⚠️ `performance.now()`, not `Date.now()`: it is monotonic, so a laptop
   * waking or an NTP correction cannot make somebody suddenly starving.
   */
  private syncedAt: number | null = null;
  /** The reason the server sent with it — a key, or `null` for nothing to say. */
  private reasonAtSync: VitalsReasonKey = null;
  /**
   * 🥶 What the weather is costing, as the server last worked it out (T131).
   *
   * 🔴 **Taken from the server rather than computed here, and that is the whole
   * point.** The client can answer the shelter question itself — it has the
   * chunks — but its chunks answer AIR for anything still streaming in, so its
   * answer can be briefly wrong. **The server owns the world and charges the
   * drain, so the server owns this.** A gauge that disagreed with the database
   * would be the confusing kind of bug.
   *
   * ⚠️ **1 until the server says otherwise** — nobody is cold before they have
   * arrived.
   */
  private exposureAtSync = 1;
  /** Which of cold, wind and wet are costing something. Empty is comfortable. */
  private bitingAtSync: readonly ExposureFactor[] = [];

  /**
   * Take the server's answer.
   *
   * ⚠️ **The server always wins, in both directions.** A correction ought to be
   * a fraction of a percent, because both ends ran the same function over the
   * same elapsed time. 🔴 **If it is ever large, that is a real disagreement and
   * it should be visible** — keeping the local number because it looked
   * smoother would hide exactly the bug worth finding.
   */
  sync(
    vitals: Vitals,
    reasonKey: string | null,
    at: number,
    exposure = 1,
    biting: readonly string[] = [],
  ): void {
    this.atSync = vitals;
    this.syncedAt = at;
    this.reasonAtSync = asReasonKey(reasonKey);
    /* ⚠️ Anything below 1 is refused here as well as in `stepVitals`. A wire is
       a place values arrive from outside, and the rule is cheaper to keep
       twice than to explain once. */
    this.exposureAtSync = Number.isFinite(exposure) ? Math.max(1, exposure) : 1;
    this.bitingAtSync = biting.filter(isExposureFactor);
  }

  /** 🛏️ D133 — asleep: the server freezes the bars, so the screen must too. */
  private frozen = false;

  /**
   * 🛏️ **Stop or restart the carry-forward** — D133. Without this the bars would
   * fall on screen all night and jump back at every server beat, which reads as
   * the sleep not working. Restarting counts from NOW, so nothing is charged
   * for the time asleep — exactly what the server does.
   */
  setFrozen(frozen: boolean, at: number): void {
    if (frozen === this.frozen) return;
    if (frozen) this.atSync = this.now(at) ?? this.atSync;
    this.syncedAt = this.atSync === null ? this.syncedAt : at;
    this.frozen = frozen;
  }

  /**
   * The bars now, or `null` if the server has not said yet.
   *
   * @param at `performance.now()`
   */
  now(at: number): Vitals | null {
    if (this.atSync === null || this.syncedAt === null) return null;
    if (this.frozen) return this.atSync;
    /* 🥶 Carried forward with the server's own multiplier, so a player standing
       in a storm watches the bar fall at the rate they are actually being
       charged rather than at the calm one. */
    return stepVitals(this.atSync, Math.max(0, at - this.syncedAt), {
      exposure: this.exposureAtSync,
    });
  }

  /** What the food drain is being multiplied by. 1 is comfortable. */
  get exposure(): number {
    return this.exposureAtSync;
  }

  /**
   * 🌡️ Which of cold, wind and wet are costing something, worst first.
   *
   * ⚠️ **Empty when nothing is biting, and empty is not the same as unknown** —
   * a factor tuned to cost nothing never appears, which is why the screen
   * cannot blame the rain before rain has a price.
   */
  get biting(): readonly ExposureFactor[] {
    return this.bitingAtSync;
  }

  /**
   * Why the bars are doing what they are doing, as a **key** — or `null`.
   *
   * ⚠️ **Recomputed locally rather than held from the sync**, so that *starving*
   * appears the moment the food bar empties rather than up to thirty seconds
   * later. 🔴 The reason is the one thing on this cluster a player acts on:
   * §18 of the gameplay notes is blunt that with a health bar alone you cannot
   * tell starving from injured, and a warning that is half a minute late is a
   * warning about something that has already happened.
   *
   * The server's own answer is kept as the fallback for the frames before the
   * first carry-forward, so the two can never disagree at the moment of a sync.
   */
  reason(at: number): VitalsReasonKey {
    const vitals = this.now(at);
    return vitals === null ? this.reasonAtSync : vitalsReason(vitals);
  }
}

/**
 * The wire carries `string | null`, because a key from outside is text until it
 * is checked. ⚠️ **An unknown key becomes `null` rather than being shown**: the
 * bars are still right, and a raw `hud.reason.something` under them would say
 * less than nothing.
 */
function asReasonKey(value: string | null): VitalsReasonKey {
  return value === 'hungry' || value === 'starving' || value === 'recovering' ? value : null;
}

/** Same rule for the exposure factors: unknown text off the wire is dropped. */
function isExposureFactor(value: string): value is ExposureFactor {
  return (EXPOSURE_FACTORS as readonly string[]).includes(value);
}
